import { ReactElement, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select as MuiSelect,
  OutlinedInput,
} from '@breathelife/mui';
import { QuestionnaireVersionFlag } from '@breathelife/types';

import { useCarrierContext } from '../../../../Hooks';
import { useUpdateQuestionnaireVersionMutation } from '../../../../ReactQuery/Admin/Questionnaire/questionnaireVersion.mutations';
import { QuestionnaireVersionDetailWithNodeIdInfo } from '../../../../Services/QuestionnaireVersionService';
import { QuestionnaireEditorContext } from '../ContextProvider/QuestionnaireEditorContextProvider';

export function QuestionnaireVersionFlagsSelection({
  questionnaireVersionData,
  isDisabled,
}: {
  questionnaireVersionData?: QuestionnaireVersionDetailWithNodeIdInfo;
  isDisabled?: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const { features } = useCarrierContext();

  const { setIsMutating } = useContext(QuestionnaireEditorContext);

  const { mutate: updateQuestionnaireVersion } = useUpdateQuestionnaireVersionMutation(questionnaireVersionData?.id);

  const [questionnaireVersionFlags, setQuestionnaireVersionFlags] = useState<QuestionnaireVersionFlag[]>([]);

  useEffect(() => {
    setQuestionnaireVersionFlags(questionnaireVersionData?.flags ?? []);
  }, [questionnaireVersionData]);

  const availableFlags = useMemo(
    () => (features.questionnaireVersionFlags.enabled ? features.questionnaireVersionFlags.flags : []),
    [features],
  );

  const flagOptions = useMemo(
    () => (availableFlags ? availableFlags.map((flag) => ({ value: flag, label: flag })) : []),
    [availableFlags],
  );

  const handleFlagChange = async (event: any): Promise<void> => {
    const { value } = event.target;
    const newFlags = typeof value === 'string' ? (value.split(',') as QuestionnaireVersionFlag[]) : value;
    setQuestionnaireVersionFlags(newFlags);
    setIsMutating(true);
    updateQuestionnaireVersion(
      {
        flags: newFlags,
      },
      {
        onSettled: () => {
          setIsMutating(false);
        },
      },
    );
  };

  return (
    <FormControl fullWidth size='small' sx={{ minWidth: 200 }}>
      <InputLabel id='questionnaire-flags-label'>{t('admin.questionnaireManagement.selectedFlags')}</InputLabel>
      <MuiSelect
        labelId='questionnaire-flags-label'
        multiple
        value={questionnaireVersionFlags}
        onChange={handleFlagChange}
        input={<OutlinedInput label={t('admin.questionnaireManagement.selectedFlags')} />}
        renderValue={(selected) => t('admin.questionnaireManagement.selectedFlagsLabel', { count: selected.length })}
      >
        {flagOptions.map((option) => (
          <MenuItem key={option.value} value={option.value} disabled={isDisabled}>
            <Checkbox checked={questionnaireVersionFlags.includes(option.value)} />
            <ListItemText primary={option.label} />
          </MenuItem>
        ))}
      </MuiSelect>
    </FormControl>
  );
}
